import { z } from 'zod'

// ============================================================================
// Node Execution Types
// ============================================================================

/**
 * Status of a single node execution in the orchestrator workflow
 */
export type NodeStatus = 'pending' | 'running' | 'complete' | 'error' | 'skipped'

/**
 * Phase of execution in the orchestrator workflow
 * - orchestration: Planning and routing decisions
 * - delegation: Handing off to specialized agents
 * - execution: Actual task execution by agents
 */
export type NodePhase = 'orchestration' | 'delegation' | 'execution'

/**
 * Represents a single node's execution state
 */
export interface NodeExecution {
  nodeId: string
  nodeName: string
  agentId?: string
  agentName?: string
  phase: NodePhase
  status: NodeStatus
  startedAt: number
  completedAt?: number
  durationMs?: number
  resultPreview?: string
  error?: string
}

// ============================================================================
// HITL Interrupt Types
// ============================================================================

/**
 * Payload sent when a workflow requires human approval
 */
export interface InterruptPayload {
  question: string
  context: Record<string, unknown>
  options?: {
    approve_label?: string
    reject_label?: string
    allow_modification?: boolean
    modifiable_params?: string[]
  }
  metadata?: {
    agent_id?: string
    node_id?: string
    action_type?: string
    risk_level?: 'low' | 'medium' | 'high'
  }
}

/**
 * SSE event received when workflow pauses for approval
 */
export interface InterruptEvent {
  type: 'approval_required' | 'confirmation_required' | 'input_required'
  thread_id: string
  interrupt_id: string
  payload: InterruptPayload
  timestamp: number
}

/**
 * Decision sent back to resume the workflow
 */
export interface ApprovalDecision {
  approved: boolean
  notes?: string
  modified_params?: Record<string, unknown>
  decided_by?: string
}

// ============================================================================
// Enterprise Status Types
// ============================================================================

/**
 * Status of enterprise features from the orchestrator
 */
export interface EnterpriseStatus {
  orchestrator_connected: boolean
  policy_engine_enabled: boolean
  hitl_enabled: boolean
  executor_framework_enabled: boolean
  error_recovery_enabled: boolean
  last_updated: number
}

// ============================================================================
// Orchestrator SSE Event Schemas (Zod)
// ============================================================================

/**
 * Schema for node_start SSE event
 */
export const NodeStartEventSchema = z.object({
  node_id: z.string(),
  node_name: z.string(),
  agent_id: z.string().optional(),
  agent_name: z.string().optional(),
  phase: z.enum(['orchestration', 'delegation', 'execution']),
  timestamp: z.number().optional(),
})
export type NodeStartEvent = z.infer<typeof NodeStartEventSchema>

/**
 * Schema for node_complete SSE event
 */
export const NodeCompleteEventSchema = z.object({
  node_id: z.string(),
  node_name: z.string().optional(),
  status: z.enum(['complete', 'error', 'skipped']),
  duration_ms: z.number().optional(),
  result_preview: z.string().optional(),
  error: z.string().optional(),
})
export type NodeCompleteEvent = z.infer<typeof NodeCompleteEventSchema>

/**
 * Schema for interrupt SSE event (HITL approval)
 */
export const InterruptEventSchema = z.object({
  type: z.enum(['approval_required', 'confirmation_required', 'input_required']),
  thread_id: z.string(),
  interrupt_id: z.string(),
  payload: z.object({
    question: z.string(),
    context: z.record(z.unknown()),
    options: z
      .object({
        approve_label: z.string().optional(),
        reject_label: z.string().optional(),
        allow_modification: z.boolean().optional(),
        modifiable_params: z.array(z.string()).optional(),
      })
      .optional(),
    metadata: z
      .object({
        agent_id: z.string().optional(),
        node_id: z.string().optional(),
        action_type: z.string().optional(),
        risk_level: z.enum(['low', 'medium', 'high']).optional(),
      })
      .optional(),
  }),
  timestamp: z.number().optional(),
})

/**
 * Schema for resumed SSE event (workflow continues after approval)
 */
export const ResumedEventSchema = z.object({
  thread_id: z.string(),
  interrupt_id: z.string(),
  decision: z.enum(['approved', 'rejected', 'modified']),
  timestamp: z.number().optional(),
})
export type ResumedEvent = z.infer<typeof ResumedEventSchema>

/**
 * Schema for enterprise_status SSE event
 */
export const EnterpriseStatusEventSchema = z.object({
  orchestrator_connected: z.boolean(),
  policy_engine_enabled: z.boolean().default(false),
  hitl_enabled: z.boolean().default(false),
  executor_framework_enabled: z.boolean().default(false),
  error_recovery_enabled: z.boolean().default(false),
})

/**
 * Schema for fallback SSE event (orchestrator unavailable)
 */
export const FallbackEventSchema = z.object({
  reason: z.string(),
  fallback_agent: z.string().optional(),
  original_target: z.string().optional(),
})
export type FallbackEvent = z.infer<typeof FallbackEventSchema>

// ============================================================================
// Parser Functions
// ============================================================================

export function parseNodeStartEvent(data: unknown): NodeStartEvent | null {
  const result = NodeStartEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseNodeCompleteEvent(data: unknown): NodeCompleteEvent | null {
  const result = NodeCompleteEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseInterruptEvent(data: unknown): InterruptEvent | null {
  const result = InterruptEventSchema.safeParse(data)
  if (!result.success) return null
  return {
    ...result.data,
    timestamp: result.data.timestamp ?? Date.now(),
  }
}

export function parseResumedEvent(data: unknown): ResumedEvent | null {
  const result = ResumedEventSchema.safeParse(data)
  return result.success ? result.data : null
}

export function parseEnterpriseStatusEvent(data: unknown): EnterpriseStatus | null {
  const result = EnterpriseStatusEventSchema.safeParse(data)
  if (!result.success) return null
  return {
    ...result.data,
    last_updated: Date.now(),
  }
}

export function parseFallbackEvent(data: unknown): FallbackEvent | null {
  const result = FallbackEventSchema.safeParse(data)
  return result.success ? result.data : null
}

// ============================================================================
// Orchestrator State for Zustand
// ============================================================================

/**
 * State slice for orchestrator features in the dashboard store
 */
export interface OrchestratorState {
  // Thread management
  threadId: string | null

  // Node execution tracking
  nodeExecutions: NodeExecution[]

  // HITL interrupt state
  currentInterrupt: InterruptEvent | null
  isApprovalPending: boolean

  // Enterprise features status
  enterpriseStatus: EnterpriseStatus | null

  // Fallback state
  isFallbackMode: boolean
  fallbackReason: string | null

  // Orchestrator-specific status
  isOrchestratorMode: boolean
}

/**
 * Actions for orchestrator state management
 */
export interface OrchestratorActions {
  // Thread management
  setThreadId: (threadId: string | null) => void

  // Node execution tracking
  addNodeExecution: (node: NodeExecution) => void
  updateNodeExecution: (nodeId: string, updates: Partial<NodeExecution>) => void
  clearNodeExecutions: () => void

  // HITL interrupt management
  setCurrentInterrupt: (interrupt: InterruptEvent | null) => void
  setApprovalPending: (pending: boolean) => void

  // Enterprise status
  setEnterpriseStatus: (status: EnterpriseStatus | null) => void

  // Fallback management
  setFallbackMode: (isFallback: boolean, reason?: string) => void

  // Orchestrator mode toggle
  setOrchestratorMode: (enabled: boolean) => void

  // Reset orchestrator state
  resetOrchestratorState: () => void
}

/**
 * Combined orchestrator slice type
 */
export type OrchestratorSlice = OrchestratorState & OrchestratorActions

/**
 * Initial state for orchestrator slice
 */
export const initialOrchestratorState: OrchestratorState = {
  threadId: null,
  nodeExecutions: [],
  currentInterrupt: null,
  isApprovalPending: false,
  enterpriseStatus: null,
  isFallbackMode: false,
  fallbackReason: null,
  isOrchestratorMode: false,
}

// ============================================================================
// Constants
// ============================================================================

export const MAX_NODE_HISTORY = 50
export const FALLBACK_TIMEOUT_MS = 5000
export const ORCHESTRATOR_AGENT_ID = 'orchestrator'
